import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, ShieldCheck, Lock } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  useSEO(
    "Your License & Download | Mortgage Survival Playbook 2026",
    "Your Mortgage Survival Playbook 2026 institutional license is active. Access your secure, single-use download of the deployment package.",
    { noindex: true }
  );
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const orderId = params.get("order_id");

  const [state, setState] = useState("checking"); // checking | ready | exhausted | invalid | downloading
  const [info, setInfo] = useState({ limit: 0, used: 0, remaining: 0 });

  const refreshStatus = useCallback(async () => {
    if (!token) { setState("invalid"); return; }
    try {
      const { data } = await axios.get(`${API}/download/status/${token}`);
      setInfo({ limit: data.limit, used: data.used, remaining: data.remaining });
      if (!data.valid) setState("invalid");
      else if (data.exhausted) setState("exhausted");
      else setState("ready");
    } catch (e) {
      setState("invalid");
    }
  }, [token]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const handleDownload = useCallback(async () => {
    if (state !== "ready") return;
    setState("downloading");
    try {
      const response = await axios.get(`${API}/download/${token}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MortgageSurvivalPlaybook2026.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.debug("Download failed (link likely exhausted)", e);
    } finally {
      await refreshStatus();
    }
  }, [state, token, refreshStatus]);

  const { limit, used, remaining } = info;
  const multi = limit > 1;

  return (
    <div className="ty-wrap">
      <div className="hero-bg" />
      <div className="ty-card">
        <div className="ty-icon"><CheckCircle2 size={34} /></div>
        <div className="ty-badge"><Lock size={12} /> {multi ? `${limit} secure downloads` : "Secure single-use license download"}</div>
        <div className="ty-eyebrow">Payment Confirmed</div>
        <h1 className="ty-title">Thank You. Your License Is Active.</h1>

        {state === "checking" && <p className="ty-text">Verifying your secure download link…</p>}

        {(state === "ready" || state === "downloading") && (
          <>
            <p className="ty-text">
              Your purchase of the <strong>Mortgage Survival Playbook 2026 Institutional License</strong> is complete.
              {multi
                ? <> You purchased <strong>{limit} licenses</strong> — you may download the package <strong>{remaining}</strong> more time{remaining !== 1 ? "s" : ""}.</>
                : <> Your download link is valid for <strong>one download only</strong> — please save the file securely.</>}
            </p>
            {multi && (
              <div className="dl-counter">
                <div className="dl-bar"><div className="dl-bar-fill" style={{ width: `${limit ? (used / limit) * 100 : 0}%` }} /></div>
                <div className="dl-counter-txt">{used} of {limit} downloads used · {remaining} remaining</div>
              </div>
            )}
            <button className="ty-download" onClick={handleDownload} disabled={state === "downloading"}>
              {state === "downloading"
                ? (<><span className="spinner" /> Preparing your download…</>)
                : (<><Download size={18} /> Download Your Playbook Package (ZIP)</>)}
            </button>
            <div className="ty-warn"><strong>Important:</strong> {multi ? `Each download counts against your ${limit} purchased licenses.` : "This is a one-time download tied to your payment. Once downloaded, the link expires."} Back up the ZIP file immediately.</div>
          </>
        )}

        {state === "exhausted" && (
          <>
            <p className="ty-text">You have used all <strong>{limit}</strong> download{limit !== 1 ? "s" : ""} included with your purchase.</p>
            <div className="ty-error"><ShieldCheck size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} /><strong>Download limit reached.</strong> Each payment allows exactly as many downloads as licenses purchased. For a replacement or additional licenses, email <a href="mailto:hello@mortgagesurvivalplaybook.com" style={{ color: "var(--gold)" }}>hello@mortgagesurvivalplaybook.com</a> with your order ID.</div>
          </>
        )}

        {state === "invalid" && (
          <div className="ty-error"><strong>This download link is invalid or has expired.</strong> If you completed a payment, email <a href="mailto:hello@mortgagesurvivalplaybook.com" style={{ color: "var(--gold)" }}>hello@mortgagesurvivalplaybook.com</a> with your order ID.</div>
        )}

        {orderId && (<div className="ty-meta">Order ID<div className="ty-order">{orderId}</div></div>)}
        <a className="ty-back" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>← Back to home</a>
      </div>
    </div>
  );
};

export default ThankYou;
